
import { defineComponent, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { DrawerComponent } from "@/assets/ts/components/_DrawerComponent";
import MerchantTaskDrawer from "@/layout/extras/MerchantTaskDrawer.vue";

import Dropdown3 from "@/components/merchants/Dropdown3.vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import MerchantAccountCard from "@/components/merchants/accounts/MerchantAccountCard.vue";
import ApiService from "@/core/services/ApiService";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";

interface Connection {
  id: string;
  conn_name: string;
  scheme: string;
  host: string;
  domain: string;
  auth_type: string;
  payload_type: string;
  response_type: string;
  enabled: string;
  conn_status: string;
  category: string;
  created: string;
  modified: string;
  logo_url: string;
}

interface TableData {
  data: unknown[];
  meta: {
    // eslint-disable-next-line
    total: any;
  };
}

interface MerchantAccount {
  id: string;
  merchant_id: string;
  parameters: string;
  primary_api: string;
  alias_name: string;
  category: string;
  connection_id: string;
  customer_id: string;
  enabled: string;
  endpoint_id: string;
  endpoint_name: string;
  endpoint_path: string;
  endpoint_status: string;
  group_id: string;
}

export default defineComponent({
  name: "merchants-accounts",
  components: {
    MerchantAccountCard,
    Dropdown3,
    Datatable,
    MerchantTaskDrawer,
  },

  setup() {
    onMounted(async () => {
      setCurrentPageTitle("Merchant Accounts");

      tableLoading.value = true;

      setTimeout(async () => {
        const res = await ApiService.get(`apis/connections?limit=100`);
        connections.value = res.data.data;

        await getCardAccounts();

        tableData.value = await getTableAccounts(
          tablePagination.value,
          tableSort.value
        );

        DrawerComponent.reinitialization();

        tableLoading.value = false;
      }, 500);
    });

    const store = useStore();
    const cardCurPage = ref(1);
    const cardRowsPerPage = 9;
    const cardCurRows = ref(0);
    const cardTotal = ref(0);
    const cardAccounts = ref<MerchantAccount[]>([]);
    const cardLoading = ref(false);
    const connections = ref<Connection[]>([]);
    const searchParam = ref({
      search: "",
      merchant_id: "",
      corp_name: "",
      customer_id: "",
      dba_name: "",
      merchant_progress: "",
      merchant_type: "",
      mid_number: "",
      processor_id: "",
      tpp_id: "",
    });

    const getCardAccounts = async () => {
      cardLoading.value = true;

      try {
        const offset = (cardCurPage.value - 1) * cardRowsPerPage;

        let url = `merchants/accounts?offset=${offset}&limit=${cardRowsPerPage}&group_id=${store.getters.currentUser.data.companygroupid}`;

        if (searchParam.value.search !== "") {
          url = `${url}&search[]=${searchParam.value.search}`;
        }
        if (searchParam.value.merchant_id !== "") {
          url = `${url}&merchant_id=${searchParam.value.merchant_id}`;
        }
        if (searchParam.value.corp_name !== "") {
          url = `${url}&corp_name=${searchParam.value.corp_name}`;
        }
        if (searchParam.value.customer_id !== "") {
          url = `${url}&customer_id=${searchParam.value.customer_id}`;
        }
        if (searchParam.value.dba_name !== "") {
          url = `${url}&dba_name=${searchParam.value.dba_name}`;
        }
        if (searchParam.value.merchant_progress !== "") {
          url = `${url}&merchant_progress=${searchParam.value.merchant_progress}`;
        }
        if (searchParam.value.merchant_type !== "") {
          url = `${url}&merchant_type=${searchParam.value.merchant_type}`;
        }
        if (searchParam.value.mid_number !== "") {
          url = `${url}&mid_number=${searchParam.value.mid_number}`;
        }
        if (searchParam.value.processor_id !== "") {
          url = `${url}&processor_id=${searchParam.value.processor_id}`;
        }
        if (searchParam.value.tpp_id !== "") {
          url = `${url}&tpp_id=${searchParam.value.tpp_id}`;
        }

        const { data } = await ApiService.get(url);

        cardAccounts.value = await Promise.all(
          data.data.map(async (account) => {
            try {
              account.merchantDetails = JSON.parse(account.merchantDetails);
            } catch (e) {
              account.merchantDetails = {};
            }

            try {
              const { data } = await ApiService.get(
                `apis/merchants?offset=0&limit=10&merchant_id=${account.merchant_id}`
              );
              account.apis = data.data.map((api) => {
                api.connection = connections.value.find(
                  (conn) => conn.id === api.connection_id
                );
                return api;
              });
            } catch (e) {
              account.apis = [];
            }

            const d = new Date();
            const monthName = d.toLocaleString("default", { month: "long" });

            try {
              const { data } = await ApiService.get(
                `reports/chargebacks/incoming.monthly?merchant_id=${account.merchant_id}&month=${monthName}`
              );

              account.totalChargebacks = Number(data.data[0].count);
            } catch (e) {
              account.totalChargebacks = 0;
            }

            try {
              const { data } = await ApiService.get(
                `reports/transactions/transactions.overview?merchant_id=${account.merchant_id}&action_type=sale&month=${monthName}`
              );

              account.totalSales = Number(data.data[0].count);
            } catch (e) {
              account.totalSales = 0;
            }

            try {
              if (account.totalSales === 0) {
                account.cbratio = "0";
              } else {
                account.cbratio = (
                  (account.totalChargebacks / account.totalSales) *
                  100
                ).toFixed(2);
              }
            } catch (e) {
              account.cbratio = "0";
            }

            return account;
          })
        );
        cardCurRows.value = data.data.length;
        cardTotal.value = data.meta.total;
      } catch (err) {
        cardAccounts.value = [];
        cardTotal.value = 0;
        cardCurPage.value = 1;
        cardCurRows.value = 0;
      }

      cardLoading.value = false;
    };

    const cardCurrentPageChange = (val) => {
      cardCurPage.value = val;
      getCardAccounts();
    };

    const onNewMerchantSubmit = () => {
      getCardAccounts();
    };

    const search = () => {
      cardCurPage.value = 1;
      getCardAccounts();
    };

    const getTableAccounts = async (pagination, sort) => {
      try {
        const { page, rowsPerPage } = pagination;
        const offset = (page - 1) * rowsPerPage;

        let sortParam = "";
        const { columnName, order } = sort;
        if (columnName !== "") {
          sortParam = `&sort=${columnName},${order}`;
        }

        let url = `merchants/accounts?offset=${offset}&limit=${rowsPerPage}&group_id=${store.getters.currentUser.data.companygroupid}${sortParam}`;

        if (searchParam.value.search !== "") {
          url = `${url}&search[]=${searchParam.value.search}`;
        }
        if (searchParam.value.merchant_id !== "") {
          url = `${url}&merchant_id=${searchParam.value.merchant_id}`;
        }
        if (searchParam.value.corp_name !== "") {
          url = `${url}&corp_name=${searchParam.value.corp_name}`;
        }
        if (searchParam.value.customer_id !== "") {
          url = `${url}&customer_id=${searchParam.value.customer_id}`;
        }
        if (searchParam.value.dba_name !== "") {
          url = `${url}&dba_name=${searchParam.value.dba_name}`;
        }
        if (searchParam.value.merchant_progress !== "") {
          url = `${url}&merchant_progress=${searchParam.value.merchant_progress}`;
        }
        if (searchParam.value.merchant_type !== "") {
          url = `${url}&merchant_type=${searchParam.value.merchant_type}`;
        }
        if (searchParam.value.mid_number !== "") {
          url = `${url}&mid_number=${searchParam.value.mid_number}`;
        }
        if (searchParam.value.processor_id !== "") {
          url = `${url}&processor_id=${searchParam.value.processor_id}`;
        }
        if (searchParam.value.tpp_id !== "") {
          url = `${url}&tpp_id=${searchParam.value.tpp_id}`;
        }

        const { data } = await ApiService.get(url);

        const tableAccounts = await Promise.all(
          data.data.map(async (account) => {
            try {
              account.merchantDetails = JSON.parse(account.merchantDetails);
            } catch (e) {
              account.merchantDetails = {};
            }
            try {
              const { data } = await ApiService.get(
                `processors/isos/${account.processor_id}?fields=name`
              );
              account.processorName = String(data.data[0].name);
            } catch (e) {
              account.processorName = "";
            }
            try {
              const { data } = await ApiService.get(
                `apis/merchants?offset=0&limit=10&merchant_id=${account.merchant_id}`
              );
              account.apis = data.data.map((api) => {
                api.connection = connections.value.find(
                  (conn) => conn.id === api.connection_id
                );
                return api;
              });
            } catch (e) {
              account.apis = [];
            }

            const d = new Date();
            const monthName = d.toLocaleString("default", { month: "long" });

            try {
              const { data } = await ApiService.get(
                `reports/chargebacks/incoming.monthly?merchant_id=${account.merchant_id}&month=${monthName}`
              );

              account.totalChargebacks = Number(data.data[0].count);
            } catch (e) {
              account.totalChargebacks = 0;
            }

            try {
              const { data } = await ApiService.get(
                `reports/transactions/transactions.overview?merchant_id=${account.merchant_id}&action_type=sale&month=${monthName}`
              );

              account.totalSales = Number(data.data[0].count);
            } catch (e) {
              account.totalSales = 0;
            }

            try {
              if (account.totalSales === 0) {
                account.cbratio = "0";
              } else {
                account.cbratio = (
                  (account.totalChargebacks / account.totalSales) *
                  100
                ).toFixed(2);
              }
            } catch (e) {
              account.cbratio = "0";
            }

            return account;
          })
        );

        return {
          data: tableAccounts,
          meta: {
            total: data.meta.total,
          },
        };
      } catch (err) {
        return {
          data: [],
          meta: {
            total: 0,
          },
        };
      }
    };

    const tablePagination = ref({
      page: 1,
      rowsPerPage: 5,
    });
    const tableSort = ref({
      columnName: "",
      order: "",
    });
    const checkedTableAccounts = ref([]);
    const tableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
      },
      {
        name: "ID",
        key: "merchant_id",
        sortable: true,
      },
      {
        name: "DBA",
        key: "dba_name",
        sortable: true,
      },
      {
        name: "MID",
        key: "mid_number",
        sortable: true,
      },
      {
        name: "Type",
        key: "merchant_type",
        sortable: true,
      },
      {
        name: "Phone",
        key: "merchant_phone",
        sortable: true,
      },
      {
        name: "ISO",
        key: "processorName",
        sortable: true,
      },
      {
        name: "Website",
        key: "merchant_url",
        sortable: true,
      },
      {
        name: "Actions",
        key: "actions",
      },
    ]);

    const tableData = ref<TableData>({
      data: [],
      meta: {
        total: 0,
      },
    });
    const tableLoading = ref(true);
    const selectedMerchant = ref({});

    const openDrawer = (val) => {
      selectedMerchant.value = val;
      DrawerComponent?.getInstance("merchant_task_drawer")?.show();
    };

    const didClosedDrawer = async () => {
      tableLoading.value = true;
      tableData.value = await getTableAccounts(tablePagination.value, tableSort.value);
      tableLoading.value = false;
    };

    const onTableCurrentChange = async (val) => {
      tablePagination.value.page = val;

      tableLoading.value = true;
      tableData.value = await getTableAccounts(
        tablePagination.value,
        tableSort.value
      );
      tableLoading.value = false;
    };

    const onTableSort = async (val) => {
      tableSort.value = val;

      tableLoading.value = true;
      tableData.value = await getTableAccounts(
        tablePagination.value,
        tableSort.value
      );
      tableLoading.value = false;
    };

    const onTableItemsPerPageChange = async (val) => {
      tablePagination.value.rowsPerPage = val;

      tableLoading.value = true;
      tableData.value = await getTableAccounts(
        tablePagination.value,
        tableSort.value
      );
      tableLoading.value = false;
    };

    const getStatus = (status) => {
      return status === "1" ? "On" : "Off";
    };

    const getStatusDataBadgeColor = (status) => {
      return status === "1" ? "badge-light-primary" : "badge-light-danger";
    };

    return {
      cardCurPage,
      cardRowsPerPage,
      cardCurRows,
      cardTotal,
      cardAccounts,
      didClosedDrawer,
      cardCurrentPageChange,
      cardLoading,
      onNewMerchantSubmit,
      searchParam,
      search,
      openDrawer,
      tableData,
      tableHeader,
      tableLoading,
      checkedTableAccounts,
      tablePagination,
      selectedMerchant,
      onTableCurrentChange,
      onTableSort,
      onTableItemsPerPageChange,
      getStatus,
      getStatusDataBadgeColor,
    };
  },
});
